import * as XLSX from "xlsx";

const SUPPORTED_EXTENSIONS = new Set(["xlsx", "xls", "csv"]);

const HEADER_ALIASES = {
    employeeCode: ["employeecode", "matricule", "codeemploye", "matriculeemploye"],
    username: ["username", "identifiant", "login"],
    email: ["email", "courriel", "mail"],
    firstName: ["firstname", "prenom", "givenname"],
    lastName: ["lastname", "nom", "surname", "familyname"],
    phone: ["phone", "telephone", "tel", "mobile", "phonenumber"],
    dateOfBirth: ["dateofbirth", "datenaissance", "birthdate"],
    hireDate: ["hiredate", "datedembauche", "dateembauche", "employmentdate"],
    jobCode: ["jobcode", "postcode", "codeposte", "fonctioncode"],
    entityCode: ["entitycode", "codeentite", "entitecode", "departmentcode"],
    managerEmployeeCode: ["manageremployeecode", "managercode", "managermatricule", "matriculemanager"],
    status: ["status", "statut"],
    typeContratCode: ["typecontratcode", "typecontrat", "contrat", "contracttype"],
    category: ["category", "categorie"],
    remunerationRib: ["remunerationrib", "rib", "ribremuneration"],
    remunerationBanque: ["remunerationbanque", "banque"],
    remunerationAgence: ["remunerationagence", "agence"],
    remunerationTaux: ["remunerationtaux", "taux"],
};

const IMPORT_FIELDS = Object.keys(HEADER_ALIASES);

const normalizeHeader = (value) =>
    String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .toLowerCase()
        .trim();

const normalizeCellValue = (value) => {
    if (value == null) return "";
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return formatDate(value);
    }
    return String(value).trim();
};

const formatDate = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const parseDateValue = (value) => {
    const normalized = normalizeCellValue(value);
    if (!normalized) return null;

    if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(normalized)) {
        return normalized.replace(/\//g, "-");
    }

    const frDateMatch = normalized.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
    if (frDateMatch) {
        const [, day, month, year] = frDateMatch;
        return `${year}-${month}-${day}`;
    }

    const jsDate = new Date(normalized);
    return Number.isNaN(jsDate.getTime()) ? normalized : formatDate(jsDate);
};

const parseDecimalValue = (value) => {
    const normalized = normalizeCellValue(value);
    if (!normalized) return null;
    const parsed = Number(normalized.replace(",", "."));
    return Number.isNaN(parsed) ? null : parsed;
};

const isMeaningfulRow = (row) => row.some((cell) => normalizeCellValue(cell) !== "");

const buildHeaderMap = (headerRow) => {
    const normalizedHeaders = headerRow.map(normalizeHeader);

    return Object.fromEntries(
        IMPORT_FIELDS.map((field) => {
            const index = normalizedHeaders.findIndex((header) =>
                HEADER_ALIASES[field].includes(header)
            );
            return [field, index];
        })
    );
};

const hasRecognizedHeaders = (headerMap) =>
    Object.values(headerMap).some((index) => index >= 0);

const getCell = (row, index) => (index >= 0 ? row[index] : "");

const toNullableString = (value) => {
    const normalized = normalizeCellValue(value);
    return normalized || null;
};

const readFileAsArrayBuffer = async (file) => {
    if (typeof file.arrayBuffer === "function") {
        return file.arrayBuffer();
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error("Impossible de lire le fichier."));
        reader.readAsArrayBuffer(file);
    });
};

export const parseUserImportFile = async (file) => {
    if (!file) {
        return { ok: false, errorType: "no_file", message: "Veuillez sélectionner un fichier." };
    }

    if (file.size <= 0) {
        return { ok: false, errorType: "empty_file", message: "Le fichier sélectionné est vide." };
    }

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    if (!SUPPORTED_EXTENSIONS.has(extension)) {
        return { ok: false, errorType: "unsupported_format", message: "Format de fichier non supporté." };
    }

    const data = await readFileAsArrayBuffer(file);
    const workbook = XLSX.read(data, { type: "array", cellDates: true, raw: false });
    const firstSheetName = workbook.SheetNames?.[0];

    if (!firstSheetName) {
        return { ok: false, errorType: "empty_file", message: "Le fichier sélectionné est vide." };
    }

    const sheet = workbook.Sheets[firstSheetName];
    const matrix = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: "",
        raw: false,
        blankrows: true,
    });

    const firstMeaningfulRowIndex = matrix.findIndex(isMeaningfulRow);
    if (firstMeaningfulRowIndex < 0) {
        return { ok: false, errorType: "empty_file", message: "Le fichier sélectionné est vide." };
    }

    const headerRow = matrix[firstMeaningfulRowIndex] || [];
    const headerMap = buildHeaderMap(headerRow);
    if (!hasRecognizedHeaders(headerMap)) {
        return { ok: false, errorType: "unsupported_format", message: "Format de fichier non supporté." };
    }

    const rawDataRows = matrix.slice(firstMeaningfulRowIndex + 1);
    const nonEmptyDataRows = rawDataRows.filter(isMeaningfulRow);

    if (!nonEmptyDataRows.length) {
        return {
            ok: false,
            errorType: "header_only",
            message: "Le fichier ne contient aucun utilisateur à importer.",
        };
    }

    const rows = nonEmptyDataRows
        .map((row) => ({
            employeeCode: toNullableString(getCell(row, headerMap.employeeCode)),
            username: toNullableString(getCell(row, headerMap.username)),
            email: toNullableString(getCell(row, headerMap.email)),
            firstName: toNullableString(getCell(row, headerMap.firstName)),
            lastName: toNullableString(getCell(row, headerMap.lastName)),
            phone: toNullableString(getCell(row, headerMap.phone)),
            dateOfBirth: parseDateValue(getCell(row, headerMap.dateOfBirth)),
            hireDate: parseDateValue(getCell(row, headerMap.hireDate)),
            jobCode: toNullableString(getCell(row, headerMap.jobCode)),
            entityCode: toNullableString(getCell(row, headerMap.entityCode)),
            managerEmployeeCode: toNullableString(getCell(row, headerMap.managerEmployeeCode)),
            status: toNullableString(getCell(row, headerMap.status))?.toUpperCase() || null,
            typeContratCode: toNullableString(getCell(row, headerMap.typeContratCode)),
            category: toNullableString(getCell(row, headerMap.category))?.toUpperCase() || null,
            remunerationRib: toNullableString(getCell(row, headerMap.remunerationRib)),
            remunerationBanque: toNullableString(getCell(row, headerMap.remunerationBanque)),
            remunerationAgence: toNullableString(getCell(row, headerMap.remunerationAgence)),
            remunerationTaux: parseDecimalValue(getCell(row, headerMap.remunerationTaux)),
        }))
        .filter((row) =>
            Object.values(row).some((value) => value !== null && value !== "")
        );

    if (!rows.length) {
        return {
            ok: false,
            errorType: "header_only",
            message: "Le fichier ne contient aucun utilisateur à importer.",
        };
    }

    return {
        ok: true,
        rows,
        totalRows: nonEmptyDataRows.length,
        ignoredRows: nonEmptyDataRows.length - rows.length,
    };
};
